export type BrandKey = 'payup' | 'billroutePay';

export interface Brand {
  key: BrandKey;
  displayName: string;
  /**
   * The legal contracting entity. BillRoute Pay is a d/b/a of PayUp, Inc., so
   * this stays 'PayUp, Inc.' for BOTH brands. Used by agreement/ToS/entity
   * copy via i18n {{brandLegalEntity}} — never set it to the display name.
   */
  legalEntity: string;
  supportEmail: string;
  /** Brand marketing site, used in outbound links/copy (never the app host). */
  marketingUrl: string;
  /** Terms link shown under the "Text message" consent line. */
  termsUrl: string;
  /**
   * Privacy policy link shown under the "Text message" consent line. Required:
   * the text-message consent line must link a privacy policy on every brand.
   */
  privacyUrl: string;
  /**
   * Text-message consent on the sign-up review step. `true`: the review step
   * shows its own "Text message" box with the consent line, Submit saves the
   * box, and the terms sentence above Submit asks only about Stripe's texts.
   * `false` keeps the PayUp review step as it is. Matches the server's brand
   * setting of the same name, which sends texts only while the box is on.
   */
  smsConsentRules: boolean;
  /** Resolves the global font-family token in globalStyles. */
  fontFamily: string;
  palette: {
    /** COLORS.blue.500 / COLORS.primary.main / --primaryBlue */
    brand: string;
    /** Maps onto the existing COLORS.blue.* keys, brightest→lightest. */
    blueRamp: Record<'500' | '400' | '300' | '200' | '100' | '75' | '50', string>;
    /** globalStyles --primaryBlue value. */
    primaryBlueVar: string;
  };
  /** Consumed by LogoImage to pick the per-brand logo art. */
  logo: 'payup' | 'billroutePay';
}

const BRANDS: Record<BrandKey, Brand> = {
  payup: {
    key: 'payup',
    displayName: 'PayUp',
    legalEntity: 'PayUp, Inc.',
    supportEmail: 'support@payup.com',
    marketingUrl: 'https://payup.com',
    // The PayUp terms document this app already links in FooterNote.
    termsUrl: 'https://payup-docs.s3.us-east-2.amazonaws.com/PayUp+TOS+2022_04_22.pdf',
    privacyUrl: 'https://www.payup.com/privacy',
    smsConsentRules: false,
    fontFamily: "'Public Sans', sans-serif",
    palette: {
      brand: '#635BFF',
      blueRamp: {
        500: '#635BFF',
        400: '#726AFF',
        300: '#8C86FF',
        200: '#BDBAFF',
        100: '#DAD8FF',
        75: '#EDECFF',
        50: '#F7F8FF',
      },
      primaryBlueVar: '#635BFF',
    },
    logo: 'payup',
  },
  billroutePay: {
    key: 'billroutePay',
    displayName: 'BillRoute Pay',
    // DBA — do NOT change to 'BillRoute Pay, Inc.'.
    legalEntity: 'PayUp, Inc.',
    supportEmail: 'support@billroute.com',
    marketingUrl: 'https://billroute.com',
    termsUrl: 'https://www.billroute.com/terms',
    privacyUrl: 'https://www.billroute.com/privacy',
    smsConsentRules: true,
    fontFamily: "'Geist', sans-serif",
    palette: {
      // Purple ramp from billroute-design tokens/colors.css, mapped onto the
      // existing blue keys: 500=brand base (purple-600), lightening to 50.
      brand: '#9810fa',
      blueRamp: {
        500: '#9810fa',
        400: '#a855f7',
        300: '#c084fc',
        200: '#d8b4fe',
        100: '#e9d5ff',
        75: '#f3e8ff',
        50: '#faf5ff',
      },
      primaryBlueVar: '#9810fa',
    },
    logo: 'billroutePay',
  },
};

/**
 * Maps a serving hostname to its brand. The vendor app is served on both PayUp
 * and BillRoute Pay hosts from one build, so the host — not a build-time flag —
 * is the primary brand selector at runtime.
 */
const HOST_BRAND: Record<string, BrandKey> = {
  'pay.billroute.com': 'billroutePay',
  'pay.dev.billroute.com': 'billroutePay',
  'pay.qa.billroute.com': 'billroutePay',
  'app.payup.com': 'payup',
  'app.dev.payup.com': 'payup',
  'app.qa.payup.com': 'payup',
};

export const getBrand = (): Brand => {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const byHost = HOST_BRAND[host];
  if (byHost) return BRANDS[byHost];
  const key = process.env.REACT_APP_BRAND_KEY as BrandKey | undefined;
  return (key && BRANDS[key]) || BRANDS.payup;
};

/** Resolved once at page load (host-based, REACT_APP_BRAND_KEY as fallback). */
export const brand: Brand = getBrand();
